"use client"

import { useState, useEffect, useCallback, useMemo } from "react"
import axios from "axios"
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Grid,
  Card,
  Checkbox,
  BottomNavigation,
  BottomNavigationAction,
  Avatar,
  Snackbar,
  Alert,
  IconButton,
  Paper,
  InputBase,
} from "@mui/material"
import {
  Home as HomeIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  ExitToApp as ExitToAppIcon,
  Payment as PaymentIcon,
  CalendarToday as CalendarTodayIcon,
  Search as SearchIcon,
} from "@mui/icons-material"
import { useNavigate } from "react-router-dom"

const baseUrl = process.env.REACT_APP_APP_URL

const Checklist = () => {
  const navigate = useNavigate()
  const [state, setState] = useState({
    users: [],
    seances: [],
    error: null,
    currentDate: new Date(),
    searchQuery: "",
    isSearchFocused: false,
  })

  const token = localStorage.getItem("CC_Token")

  const filteredUsers = useMemo(
    () => state.users.filter((user) => user.name.toLowerCase().includes(state.searchQuery.toLowerCase())),
    [state.users, state.searchQuery],
  )

  const getTodayDate = useCallback(() => {
    console.log("Getting today date...")
    const now = new Date()
    // Format the date in YYYY-MM-DD format using local time
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, "0")
    const day = String(now.getDate()).padStart(2, "0")
    const formattedDate = `${year}-${month}-${day}`
    console.log("Formatted today date:", formattedDate)
    return formattedDate
  }, [])

  const isUserPresent = useCallback(
    (userId) => {
      const todayDate = getTodayDate()
      console.log("isUserPresent - todayDate:", todayDate)
      console.log(
        "isUserPresent - seances:",
        state.seances.map((s) => ({
          userId: s.user_id,
          time: s.time,
          matches: s.time.startsWith(todayDate),
        })),
      )
      return state.seances.some((seance) => seance.user_id === userId && seance.time.startsWith(todayDate))
    },
    [state.seances, getTodayDate],
  )

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, seancesRes] = await Promise.all([
        axios.get(`${baseUrl}/api/users`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${baseUrl}/api/seances`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ])
      console.log(
        "fetchData - received seances:",
        seancesRes.data.map((s) => ({
          id: s.id,
          userId: s.user_id,
          time: s.time,
        })),
      )

      const activeMembers = usersRes.data.filter(
        (user) => user.role === "member" && user.isActive === 1 && user.isCavalier === 1,
      )

      setState((prev) => ({
        ...prev,
        users: activeMembers,
        seances: seancesRes.data,
      }))
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: "Erreur lors de la récupération des données",
        users: [],
        seances: [],
      }))
      handleSnackbarOpen()
    }
  }, [token])

  const handleStatusToggle = async (userId) => {
    try {
      const todayDate = getTodayDate()
      console.log("handleStatusToggle - todayDate:", todayDate)
      const isPresent = isUserPresent(userId)

      if (!isPresent) {
        await axios.post(
          `${baseUrl}/api/seances`,
          { date: todayDate, user_id: userId },
          { headers: { Authorization: `Bearer ${token}` } },
        )
      } else {
        const targetSeance = state.seances.find((s) => s.user_id === userId && s.time.startsWith(todayDate))

        if (targetSeance) {
          console.log("handleStatusToggle - targetSeance:", targetSeance)
          await axios.delete(`${baseUrl}/api/seances/${targetSeance.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        }
      }
      await fetchData()
    } catch (err) {
      setState((prev) => ({ ...prev, error: "Erreur de mise à jour du statut" }))
      handleSnackbarOpen()
      await fetchData()
    }
  }

  const handleSearchChange = (e) => {
    setState((prev) => ({ ...prev, searchQuery: e.target.value }))
  }

  const handleSnackbarOpen = () => {
    setState((prev) => ({ ...prev, openSnackbar: true }))
  }

  const handleSnackbarClose = () => {
    setState((prev) => ({ ...prev, openSnackbar: false }))
  }

  useEffect(() => {
    fetchData()

    // Update current date every minute
    const minuteTimer = setInterval(() => setState((prev) => ({ ...prev, currentDate: new Date() })), 60000)

    // Calculate time until next midnight
    const now = new Date()
    const tomorrow = new Date(now)
    tomorrow.setDate(tomorrow.getDate() + 1)
    tomorrow.setHours(0, 0, 0, 0)
    const timeUntilMidnight = tomorrow.getTime() - now.getTime()

    // Set up midnight refresh
    const midnightTimer = setTimeout(() => {
      console.log("Midnight reached - refreshing data...")
      fetchData()
      // Recursively set up next day's refresh
      setState((prev) => ({ ...prev, currentDate: new Date() }))
    }, timeUntilMidnight)

    // Cleanup timers
    return () => {
      clearInterval(minuteTimer)
      clearTimeout(midnightTimer)
    }
  }, [fetchData])

  const handleLogout = () => {
    localStorage.removeItem("CC_Token")
    navigate("/login")
  }
  const handleNavigateToNotifications = () => {
    navigate("/AdminGetNotifs");
  };
  const handleProfile = () => {
    navigate("/ProfileAdmin");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* Header */}
      <AppBar position="fixed" sx={{ backgroundColor: "#003366" }}>
        <Toolbar>
          <Avatar
            alt="Logo"
            src={"../assets/cej.png"}
            sx={{ width: 40, height: 40, marginRight: "0.5rem" }}
          />
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontSize: { xs: "1rem", sm: "1.25rem" },
              color: "#ffffff",
            }}
          >
            Club Équestre Jafoura
          </Typography>
          <IconButton color="inherit" onClick={handleNavigateToNotifications}>
            <NotificationsIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleProfile}>
            <AccountCircleIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleLogout}>
            <ExitToAppIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8, mb: 8 }}>
        {/* Date Display */}
        <Paper
          elevation={3}
          sx={{
            bgcolor: "#003366",
            color: "white",
            p: 2,
            mb: 3,
            textAlign: "center",
            borderRadius: 2,
          }}
        >
          <Typography variant="h6">
            {state.currentDate.toLocaleDateString("fr-FR", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </Typography>
        </Paper>

        {/* Search Bar */}
        <Box sx={{ mb: 4, px: 2 }}>
          <Paper
            sx={{
              p: "2px 4px",
              display: "flex",
              alignItems: "center",
              borderRadius: 2,
              boxShadow: state.isSearchFocused ? 3 : 1,
            }}
          >
            <InputBase
              placeholder="Rechercher un cavalier..."
              sx={{ ml: 2, flex: 1 }}
              value={state.searchQuery}
              onChange={handleSearchChange}
              onFocus={() => setState((prev) => ({ ...prev, isSearchFocused: true }))}
              onBlur={() => setState((prev) => ({ ...prev, isSearchFocused: false }))}
            />
            <IconButton sx={{ p: 1 }}>
              <SearchIcon />
            </IconButton>
          </Paper>
        </Box>

        {/* Users Grid */}
        <Grid container spacing={2} sx={{ px: 2 }}>
          {filteredUsers.length === 0 ? (
            <Box
              sx={{
                width: "100%",
                textAlign: "center",
                p: 4,
                color: "text.secondary",
              }}
            >
              <Typography variant="h6">
                {state.searchQuery ? "Aucun cavalier trouvé" : "Aucun cavalier disponible"}
              </Typography>
            </Box>
          ) : (
            filteredUsers.map((user) => (
              <Grid item xs={12} sm={6} md={4} key={user.id}>
                <Card
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    bgcolor: isUserPresent(user.id) ? "#e8f5e9" : "background.paper",
                  }}
                >
                  <Avatar src={user.avatar} sx={{ width: 56, height: 56, mr: 2 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {user.name}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                      {isUserPresent(user.id) ? (
                        <CheckIcon color="success" sx={{ fontSize: 20, mr: 1 }} />
                      ) : (
                        <CloseIcon color="warning" sx={{ fontSize: 20, mr: 1 }} />
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {isUserPresent(user.id) ? "Présent" : "Absent"}
                      </Typography>
                    </Box>
                  </Box>
                  <Checkbox
                    checked={isUserPresent(user.id)}
                    onChange={() => handleStatusToggle(user.id)}
                    color="success"
                  />
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      </Box>

      {/* Bottom Navigation */}
      <Box sx={{ position: "fixed", bottom: 0, width: "100%" }}>
              <BottomNavigation
                sx={{
                  backgroundColor: "#003366",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <BottomNavigationAction
                  label="Calendrier"
                  icon={<CalendarTodayIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
                  sx={{ color: "#ffffff", minWidth: "auto" }}
                  onClick={() => navigate("/AdminChrono")}
                />
                <BottomNavigationAction
                  label="Home"
                  icon={<HomeIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
                  sx={{ color: "#ffffff", minWidth: "auto" }}
                  onClick={() => navigate("/Admin")}
                />
                <BottomNavigationAction
                  label="Paiement"
                  icon={<PaymentIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
                  sx={{ color: "#ffffff", minWidth: "auto" }}
                  onClick={() => navigate("/AdminPaiment")}
                />
              </BottomNavigation>
            </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={state.openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" variant="filled" onClose={handleSnackbarClose} sx={{ width: "100%" }}>
          {state.error}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default Checklist

